<template>
  <section class="card card-absolute rfo-card rfo-card--secondary oa-section-card oa-audit">
    <div
      class="card-header bg-secondary text-white oa-audit__header"
      role="button"
      tabindex="0"
      :aria-expanded="expanded"
      @click="toggle"
      @keydown.enter="toggle"
      @keydown.space.prevent="toggle"
    >
      <h5 class="mb-0 text-white flex-grow-1">
        <i class="fa fa-history me-2" aria-hidden="true"></i>Audit Logs
      </h5>
      <span aria-hidden="true">
        <i class="fa" :class="expanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      </span>
    </div>
    <div class="card-body oa-audit__body">
      <p class="oa-audit__intro">
        Immutable history of invoice-generation requests and Odoo payment updates submitted from
        this page.
      </p>

      <div class="d-flex justify-content-between align-items-center gap-2 mb-3">
        <span v-if="uuid" class="small text-muted">
          Order
          <a
            :href="`/ryd-customer-support/find-data-by-order-uuid?uuid=${encodeURIComponent(uuid)}`"
            target="_blank"
            rel="noopener"
            class="text-decoration-none"
            title="Open Order Details in Customer Support (new tab)"
          >
            <code>{{ uuid }}</code>
            <i class="fa fa-external-link ms-1 small" aria-hidden="true"></i>
          </a>
        </span>
        <span v-else class="small text-muted">Look up an order to load its audit history.</span>
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="loading || !uuid"
          @click="load"
        >
          <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="fa fa-refresh me-1"></i>Refresh
        </button>
      </div>

      <template v-if="expanded">
        <div v-if="error" class="alert alert-danger outline mb-0">{{ error }}</div>
        <div v-else-if="loading && !entries.length" class="text-muted small py-3">
          Loading audit logs…
        </div>
        <div v-else-if="!uuid" class="alert alert-light border mb-0">
          Enter an order UUID above to view audit logs.
        </div>
        <div v-else-if="!entries.length" class="alert alert-light border mb-0">
          No Odoo payment mutations have been recorded for this order.
        </div>
        <div v-else class="table-responsive">
          <table class="table table-sm table-bordered table-striped table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Actor</th>
                <th>Result</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in entries" :key="entry.attemptId">
                <td class="small text-nowrap">
                  <ZonedDateTime :value="entry.createdAt" :zone="userTimezone" kind="user" />
                </td>
                <td>
                  <code>{{ actionLabel(entry.action) }}</code>
                </td>
                <td class="small">{{ entry.actorDisplayName || entry.actor || '—' }}</td>
                <td>
                  <span class="badge" :class="resultClass(entry.result)">{{ entry.result }}</span>
                </td>
                <td class="small">
                  {{ entry.errorDetails || entry.reason || 'Completed successfully' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { apiClient, API_PATHS } from '@/services/api';
import ZonedDateTime from '@/components/common/ZonedDateTime.vue';
import { getUserAddDataFromLocalStorage } from '@/utils/userUtils';
import { resolveUserTimezone } from '@/utils/timezoneDisplay';

const props = defineProps<{ uuid: string }>();
const userTimezone = resolveUserTimezone((getUserAddDataFromLocalStorage() || {}).timezone);
const expanded = ref(true);
const loading = ref(false);
const error = ref('');
const entries = ref<any[]>([]);
const AUDIT_ACTIONS = new Set(['triggerOdooInvoiceGeneration', 'updateOdooPayment']);

async function load() {
  if (!props.uuid) {
    entries.value = [];
    return;
  }
  loading.value = true;
  error.value = '';
  try {
    const { data } = await apiClient.rpIntegration.get(
      API_PATHS.rpIntegration.payOperations.orderAmendments.attempts(props.uuid),
    );
    entries.value = (data?.attempts ?? []).filter((entry: any) => AUDIT_ACTIONS.has(entry.action));
  } catch (err: any) {
    error.value = err?.response?.data?.message || err?.message || 'Failed to load audit logs.';
  } finally {
    loading.value = false;
  }
}

function toggle() {
  expanded.value = !expanded.value;
  if (expanded.value && props.uuid && !entries.value.length) load();
}

function actionLabel(action: string) {
  if (action === 'triggerOdooInvoiceGeneration') return 'Request Invoice Generation';
  if (action === 'updateOdooPayment') return 'Update Odoo Payment';
  return action;
}

function resultClass(result: string) {
  if (result === 'success') return 'bg-success';
  if (result === 'partial') return 'bg-warning text-dark';
  return 'bg-danger';
}

watch(() => props.uuid, load, { immediate: true });
defineExpose({ refresh: load });
</script>

<style scoped>
.oa-audit__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
}
.oa-audit__header:focus-visible {
  outline: 2px solid #fff;
  outline-offset: -2px;
}
.oa-audit__body {
  padding-top: 3rem !important;
}
.oa-audit__intro {
  color: #475467;
  text-transform: none;
}
.oa-audit table th {
  color: #344054;
  font-weight: 800;
}
.oa-audit table th,
.oa-audit table td {
  border-color: #d0d5dd;
}
</style>
